<template>
  <div>
    <v-container fluid>
      <!-- Primary Role Selector (for multi-role users) -->
      <v-row v-if="userRoles.length > 1" class="mb-2">
        <v-col cols="12" sm="4">
          <v-select
            v-model="primaryRole"
            :items="userRoles"
            label="Acting as"
            dense
            hide-details
          />
        </v-col>
        <v-col cols="auto" class="d-flex align-center">
          <v-btn small text color="grey" @click="clearReport"
            ><v-icon small>mdi-close</v-icon> Clear</v-btn
          >
        </v-col>
      </v-row>

      <!-- Pending Claims Summary — grouped by status/role level -->
      <v-card
        v-if="
          isManager || isBranchManager || isPayrollClerk || isPayrollManager
        "
        flat
        class="mb-3 pa-2"
      >
        <div v-if="filteredPendingSections.length > 0">
          <div
            v-for="section in filteredPendingSections"
            :key="section.status"
            class="d-flex flex-wrap align-center mb-1"
          >
            <span class="caption font-weight-bold mr-2 grey--text"
              >{{ section.label }}:</span
            >
            <v-chip
              v-for="adj in section.items"
              :key="section.status + '-' + adj.adjId"
              class="mr-1 mb-1"
              :color="pendingChipColor(section.status)"
              small
              dark
              @click="quickSelectAdjuster(adj)"
            >
              {{ adj.username }} ({{ adj.count }})
            </v-chip>
          </div>
        </div>
        <span v-else class="caption grey--text">No pending claims</span>
      </v-card>

      <!-- View Mode Tabs -->
      <v-tabs :value="tabIndex" class="mb-3" slider-color="primary" centered>
        <v-tab @click.native="setTab(0)">Active Claims</v-tab>
        <v-tab @click.native="setTab(1)">Current Month</v-tab>
      </v-tabs>

      <!-- Filter Bar: Branch → Adjuster (for managers) -->
      <v-card flat class="mb-4 pa-3" v-if="isManager || isBranchManager">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="4" class="mr-2">
            <v-select
              v-model="filterBranchId"
              :items="branches"
              item-text="name"
              item-value="id"
              label="BRANCH"
              dense
              hide-details
              clearable
              @change="onBranchChange"
            />
          </v-col>
          <v-col cols="12" sm="4" class="mr-2">
            <v-select
              v-model="filterAdjId"
              :items="filteredAdjusters"
              item-text="username"
              item-value="id"
              label="ADJUSTER"
              dense
              hide-details
              clearable
              :disabled="!filterBranchId && filteredAdjusters.length === 0"
            />
          </v-col>
          <v-col cols="12" sm="2">
            <v-btn color="primary" @click="loadReport" :disabled="!filterAdjId"
              >Submit</v-btn
            >
          </v-col>
        </v-row>
      </v-card>

      <!-- Skeleton while loading -->
      <v-skeleton-loader
        v-if="loadingReport"
        type="table, article, article"
        class="mx-auto my-4"
      />

      <!-- Final Report Template -->
      <div v-else-if="reportData">
        <!-- Approved/frozen banner -->
        <v-alert v-if="reportData.frozen" type="info" dismissible class="mb-3">
          {{ reportData.frozenMessage }}
        </v-alert>

        <div id="claim-form" v-if="!reportData.frozen">
          <!-- Company Header + Status Badge (side by side) -->
          <div class="d-flex align-center mb-2">
            <div style="flex: 7; text-align: center">
              <div class="text-h6 font-weight-bold">
                ASSOCIATED ADJUSTERS SDN BHD
              </div>
              <div class="text-subtitle-1">MONTHLY REIMBURSEMENT CLAIMS</div>
              <div class="caption">
                NAME: {{ adjusterName }} — MONTH: {{ reportMonth }}
              </div>
            </div>
            <div style="flex: 3; text-align: right">
              <v-chip
                :color="statusColor(reportData.groupStatus)"
                dark
                class="font-weight-bold"
                large
              >
                <v-icon left small>mdi-information</v-icon>
                {{ statusMessage }}
              </v-chip>
            </div>
          </div>

          <!-- Section A: Initial Claims Table (18 columns) -->
          <div class="section-header d-flex justify-space-between align-center">
            <span>SECTION A — INITIAL CLAIMS</span>
            <span class="font-weight-bold"
              >TOTAL CLAIMS: {{ reportData.sectionAClaims?.length || 0 }}</span
            >
          </div>
          <table class="claim-table">
            <thead>
              <tr class="grey lighten-3">
                <th class="text-center">NO.</th>
                <th>TYPE</th>
                <th>FILE NO.</th>
                <th>VEHICLE NO.</th>
                <th>INSURER</th>
                <th>INV NO.</th>
                <th>INV AMT</th>
                <th>ADJ FEES</th>
                <th>PLACE OF LOSS</th>
                <th>TRAVEL</th>
                <th>ADD.</th>
                <th>SD & STAMPS</th>
                <th>MEDICAL</th>
                <th>POLICE</th>
                <th>JPJ</th>
                <th>MISC</th>
                <th>TYPING</th>
                <th class="text-center">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(claim, i) in filteredSectionAClaims"
                :key="claim.id || i"
                :class="{ 'duplicate-row': isDuplicateClaim(claim) }"
                style="cursor: pointer"
                @click="openClaimAction(claim)"
              >
                <td class="text-center">{{ i + 1 }}</td>
                <td>{{ getDeptSubRef(claim) }}</td>
                <td>{{ claim.casefile?.id || "" }}</td>
                <td>{{ claim.casefile?.vehicleNo?.toUpperCase() || "" }}</td>
                <td>{{ getInsurerCode(claim) }}</td>
                <td>{{ claim.invoiceNo || claim.casefile?.invNo || "" }}</td>
                <td class="text-right">
                  {{ fmt(claim.invoiceTotal || claim.casefile?.invTotal) }}
                </td>
                <td class="text-right">
                  {{ fmt(claim.feeNote || claim.casefile?.invFeeNote) }}
                </td>
                <td>{{ claim.casefile?.placeOfLoss || "" }}</td>
                <td class="text-right">{{ fmt(claim.travMileage) }}</td>
                <td class="text-right">{{ fmt(claim.additional) }}</td>
                <td class="text-right">{{ fmt(claim.sdAndStamps) }}</td>
                <td class="text-right">{{ fmt(claim.medicalReport) }}</td>
                <td class="text-right">{{ fmt(claim.policeDoc) }}</td>
                <td class="text-right">{{ fmt(claim.jpjDoc) }}</td>
                <td class="text-right">{{ fmt(claim.misc) }}</td>
                <td class="text-right">{{ fmt(claim.typing) }}</td>
                <td class="text-right font-weight-bold">
                  {{ claimTotalNum(claim).toFixed(2) }}
                </td>
              </tr>
              <tr v-if="filteredSectionAClaims.length === 0">
                <td colspan="18" class="text-center grey--text">
                  No claims found for this period
                </td>
              </tr>
              <!-- SUB-TOTAL row -->
              <tr
                v-if="reportData.sectionAClaims.length > 0"
                class="peach-bg font-weight-bold"
              >
                <td colspan="6" class="text-right">SUB-TOTAL (Section A)</td>
                <td class="text-right">{{ fmt(invAmtSum()) }}</td>
                <td class="text-right">{{ fmt(adjFeesSum()) }}</td>
                <td class="text-right"><!-- PLACE OF LOSS --></td>
                <td class="text-right">{{ fmt(sectionASum(10)) }}</td>
                <td class="text-right">{{ fmt(sectionASum(11)) }}</td>
                <td class="text-right">{{ fmt(sectionASum(12)) }}</td>
                <td class="text-right">{{ fmt(sectionASum(13)) }}</td>
                <td class="text-right">{{ fmt(sectionASum(14)) }}</td>
                <td class="text-right">{{ fmt(sectionASum(15)) }}</td>
                <td class="text-right">{{ fmt(sectionASum(16)) }}</td>
                <td class="text-right">{{ fmt(sectionASum(17)) }}</td>
                <td class="text-right">{{ fmt(sectionATotalSum()) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Section B: FOLLOW UP / SHARED FILES -->
          <div class="section-header mt-4">
            SECTION B — FOLLOW UP / SHARED FILES
          </div>
          <table class="claim-table">
            <thead>
              <tr class="grey lighten-3">
                <th>NO.</th>
                <th>TYPE</th>
                <th>FILE NO.</th>
                <th>VEHICLE NO.</th>
                <th>INSURER</th>
                <th>INV NO.</th>
                <th>INV AMT</th>
                <th>ADJ FEES</th>
                <th>PLACE OF LOSS</th>
                <th>TRAVEL</th>
                <th>ADD.</th>
                <th>SD & STAMPS</th>
                <th>MEDICAL</th>
                <th>POLICE</th>
                <th>JPJ</th>
                <th>MISC</th>
                <th>TYPING</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in sectionBRows" :key="'b-' + i">
                <td>
                  {{ i + 1 }}
                  <v-icon
                    v-if="canEditSectionB"
                    x-small
                    color="error"
                    class="ml-1"
                    @click="removeSectionBRow(i)"
                    style="cursor: pointer"
                    >mdi-close-circle</v-icon
                  >
                </td>
                <td>
                  <v-text-field
                    v-model="row.type"
                    dense
                    hide-details
                    class="compact-input"
                    :readonly="!canEditReport"
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="row.fileNo"
                    dense
                    hide-details
                    class="compact-input"
                    :readonly="!canEditReport"
                    @change="lookupCasefile(row)"
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="row.vehicleNo"
                    dense
                    hide-details
                    class="compact-input"
                    :readonly="!canEditReport"
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="row.insurer"
                    dense
                    hide-details
                    class="compact-input"
                    :readonly="!canEditReport"
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="row.invoiceNo"
                    dense
                    hide-details
                    class="compact-input"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">{{ fmt(row.invAmount) }}</td>
                <td class="text-right">{{ fmt(row.adjFees) }}</td>
                <td>
                  <v-text-field
                    v-model="row.placeOfLoss"
                    dense
                    hide-details
                    class="compact-input"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="row.travMileage"
                    dense
                    hide-details
                    type="number"
                    class="compact-input compact-number"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="row.additional"
                    dense
                    hide-details
                    type="number"
                    class="compact-input compact-number"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="row.sdAndStamps"
                    dense
                    hide-details
                    type="number"
                    class="compact-input compact-number"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="row.medicalReport"
                    dense
                    hide-details
                    type="number"
                    class="compact-input compact-number"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="row.policeDoc"
                    dense
                    hide-details
                    type="number"
                    class="compact-input compact-number"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="row.jpjDoc"
                    dense
                    hide-details
                    type="number"
                    class="compact-input compact-number"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="row.misc"
                    dense
                    hide-details
                    type="number"
                    class="compact-input compact-number"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="row.typing"
                    dense
                    hide-details
                    type="number"
                    class="compact-input compact-number"
                    :readonly="!canEditReport"
                  />
                </td>
                <td class="text-right font-weight-bold">
                  {{ rowTotal(row) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex mt-1" v-if="canEditSectionB">
            <v-btn x-small color="primary" @click="addSectionBRow"
              >+ Add Row</v-btn
            >
            <v-btn x-small color="success" class="ml-2" @click="saveSectionB"
              >Save Section B</v-btn
            >
          </div>

          <!-- GRAND TOTAL Row (Section A + B combined) -->
          <table class="claim-table mt-2">
            <tr class="peach-bg font-weight-bold">
              <td colspan="6" class="text-right">GRAND TOTAL (A + B)</td>
              <td class="text-right amount-cell">
                {{ fmt(grandInvAmtSum()) }}
              </td>
              <td class="text-right amount-cell">
                {{ fmt(grandAdjFeesSum()) }}
              </td>
              <td class="text-right"><!-- PLACE OF LOSS --></td>
              <td class="text-right amount-cell">
                {{ fmt(grandTotalCol(10)) }}
              </td>
              <td class="text-right amount-cell">
                {{ fmt(grandTotalCol(11)) }}
              </td>
              <td class="text-right amount-cell">
                {{ fmt(grandTotalCol(12)) }}
              </td>
              <td class="text-right amount-cell">
                {{ fmt(grandTotalCol(13)) }}
              </td>
              <td class="text-right amount-cell">
                {{ fmt(grandTotalCol(14)) }}
              </td>
              <td class="text-right amount-cell">
                {{ fmt(grandTotalCol(15)) }}
              </td>
              <td class="text-right amount-cell">
                {{ fmt(grandTotalCol(16)) }}
              </td>
              <td class="text-right amount-cell">
                {{ fmt(grandTotalCol(17)) }}
              </td>
              <td class="text-right">{{ fmt(grandTotalAll) }}</td>
            </tr>
          </table>

          <!-- Section C: DYNAMIC CLAIMS -->
          <div class="section-header mt-4">SECTION C — DYNAMIC CLAIMS</div>

          <!-- Extra Claims (full width, mobile responsive) -->
          <div class="extra-claims-wrapper mt-4">
            <table class="extra-table">
              <!-- SUB-TOTAL -->
              <tr>
                <td colspan="7" class="text-right font-weight-bold">
                  SUB - TOTAL
                </td>
                <td class="peach-bg text-right amount-cell font-weight-bold">
                  {{ fmt(subTotal) }}
                </td>
              </tr>

              <!-- ACCOMODATION -->
              <tr>
                <td class="text-right" style="white-space: nowrap">@ RM</td>
                <td
                  class="text-right font-weight-bold red--text"
                  style="white-space: nowrap"
                >
                  <template v-if="canEditSectionC && sectionCRow('accomodation')">
                    <v-select
                      v-model="sectionCRow('accomodation').rate"
                      :items="[80, 100]"
                      dense
                      hide-details
                      class="compact-input"
                      style="min-width: 50px"
                      @change="calcAccomTotal"
                    />
                  </template>
                  <template v-else>{{
                    fmt(sectionCRow('accomodation').rate)
                  }}</template>
                </td>
                <td class="text-right" style="white-space: nowrap">X</td>
                <td
                  class="text-right font-weight-bold red--text"
                  style="white-space: nowrap"
                >
                  <template v-if="canEditSectionC && sectionCRow('accomodation')">
                    <v-text-field
                      v-model.number="sectionCRow('accomodation').days"
                      dense
                      hide-details
                      type="number"
                      class="compact-input compact-number"
                      style="min-width: 40px"
                      @input="calcAccomTotal"
                    />
                  </template>
                  <template v-else>{{ sectionCRow('accomodation').days }}</template>
                </td>
                <td class="text-right" style="white-space: nowrap">(DAYS)</td>
                <td colspan="2" class="text-right font-weight-bold">
                  <v-btn
                    v-if="canEditSectionC"
                    x-small
                    icon
                    class="mr-1"
                    @click="triggerUpload('accomodation')"
                  >
                    <v-icon x-small>mdi-paperclip</v-icon>
                  </v-btn>
                  <span v-if="getAttachments('accomodation').length" class="caption blue--text font-weight-bold" style="cursor:pointer" @click="showFiles('accomodation')">
                    {{ getAttachments('accomodation').length }} 📎
                  </span>
                  ACCOMODATION
                </td>
                <td class="peach-bg text-right amount-cell">
                  {{ fmt(sectionCRow('accomodation').amount) }}
                </td>
              </tr>

              <!-- HANDPHONE BILL -->
              <tr>
                <td colspan="7" class="text-right font-weight-bold">
                  <v-btn
                    v-if="canEditSectionC"
                    x-small
                    icon
                    class="mr-1"
                    @click="triggerUpload('handphone')"
                  >
                    <v-icon x-small>mdi-paperclip</v-icon>
                  </v-btn>
                  <span v-if="getAttachments('handphone').length" class="caption blue--text font-weight-bold" style="cursor:pointer" @click="showFiles('handphone')">
                    {{ getAttachments('handphone').length }} 📎
                  </span>
                  HANDPHONE BILL
                </td>
                <td class="peach-bg text-right amount-cell">
                  <template v-if="canEditSectionC && sectionCRow('handphone')">
                    <v-text-field
                      v-model.number="sectionCRow('handphone').amount"
                      dense
                      hide-details
                      type="number"
                      class="compact-input compact-number text-right"
                      style="min-width: 60px"
                    />
                  </template>
                  <template v-else>{{
                    fmt(sectionCRow('handphone').amount)
                  }}</template>
                </td>
              </tr>

              <!-- MONTHLY PARKING FEES -->
              <tr>
                <td colspan="7" class="text-right font-weight-bold">
                  <v-btn
                    v-if="canEditSectionC"
                    x-small
                    icon
                    class="mr-1"
                    @click="triggerUpload('parking')"
                  >
                    <v-icon x-small>mdi-paperclip</v-icon>
                  </v-btn>
                  <span v-if="getAttachments('parking').length" class="caption blue--text font-weight-bold" style="cursor:pointer" @click="showFiles('parking')">
                    {{ getAttachments('parking').length }} 📎
                  </span>
                  MONTHLY PARKING FEES
                </td>
                <td class="peach-bg text-right amount-cell">
                  <template v-if="canEditSectionC && sectionCRow('parking')">
                    <v-text-field
                      v-model.number="sectionCRow('parking').amount"
                      dense
                      hide-details
                      type="number"
                      class="compact-input compact-number text-right"
                      style="min-width: 60px"
                    />
                  </template>
                  <template v-else>{{
                    fmt(sectionCRow('parking').amount)
                  }}</template>
                </td>
              </tr>

              <!-- PLEASE SPECIFY (dynamic rows) -->
              <tr v-for="(row, idx) in specifyRows" :key="'spec-' + idx">
                <td colspan="6" class="text-right red--text font-weight-bold">
                  <v-btn
                    v-if="canEditSectionC"
                    x-small
                    icon
                    class="mr-1"
                    @click="triggerUpload(row.rowType)"
                  >
                    <v-icon x-small>mdi-paperclip</v-icon>
                  </v-btn>
                  <span v-if="getAttachments(row.rowType).length" class="caption blue--text font-weight-bold" style="cursor:pointer" @click="showFiles(row.rowType)">
                    {{ getAttachments(row.rowType).length }} 📎
                  </span>
                  <v-icon
                    v-if="canEditSectionC"
                    x-small
                    color="error"
                    class="mr-1"
                    @click="removeSpecifyRow(idx)"
                    style="cursor:pointer"
                  >mdi-close-circle</v-icon>
                  PLEASE SPECIFY :
                </td>
                <td class="text-right font-weight-bold">
                  <template v-if="canEditSectionC">
                    <v-select
                      v-model="row.label"
                      :items="specifyOptions"
                      dense
                      hide-details
                      class="compact-input text-right-select"
                      style="min-width: 120px"
                    />
                  </template>
                  <template v-else>
                    {{ row.label || "" }}
                  </template>
                </td>
                <td class="peach-bg text-right amount-cell">
                  <template v-if="canEditSectionC">
                    <v-text-field
                      v-model.number="row.amount"
                      dense
                      hide-details
                      type="number"
                      class="compact-input compact-number text-right"
                      style="min-width: 60px"
                    />
                  </template>
                  <template v-else>{{
                    fmt(row.amount || 0)
                  }}</template>
                </td>
              </tr>

              <!-- Add Specify Row button -->
              <tr v-if="canEditSectionC">
                <td colspan="8" class="text-left pa-1">
                  <v-btn
                    x-small
                    color="primary"
                    @click="addSpecifyRow"
                    :disabled="totalSpecifyCount >= 50"
                  >
                    <v-icon x-small class="mr-1">mdi-plus</v-icon>
                    Add Specify Row
                  </v-btn>
                  <span
                    v-if="totalSpecifyCount >= 30"
                    class="caption orange--text ml-2 font-weight-bold"
                  >
                    {{ totalSpecifyCount }}/50 rows used
                  </span>
                </td>
              </tr>

              <!-- (LESS) TRAVELLING ADVANCE -->
              <tr>
                <td colspan="7" class="text-right font-weight-bold">
                  <v-btn
                    v-if="canEditSectionC"
                    x-small
                    icon
                    class="mr-1"
                    @click="triggerUpload('advance')"
                  >
                    <v-icon x-small>mdi-paperclip</v-icon>
                  </v-btn>
                  <span v-if="getAttachments('advance').length" class="caption blue--text font-weight-bold" style="cursor:pointer" @click="showFiles('advance')">
                    {{ getAttachments('advance').length }} 📎
                  </span>
                  (LESS) TRAVELLING ADVANCE
                </td>
                <td class="peach-bg text-right amount-cell">
                  <template v-if="canEditSectionC && sectionCRow('advance')">
                    <v-text-field
                      v-model.number="sectionCRow('advance').amount"
                      dense
                      hide-details
                      type="number"
                      class="compact-input compact-number"
                      style="min-width: 60px"
                    />
                  </template>
                  <template v-else>{{
                    fmt(sectionCRow('advance').amount)
                  }}</template>
                </td>
              </tr>

              <!-- AMOUNT PAYABLE -->
              <tr class="font-weight-bold">
                <td colspan="7" class="text-right">AMOUNT PAYABLE</td>
                <td class="peach-bg text-right amount-cell text-h6">
                  {{ fmt(amountPayable) }}
                </td>
              </tr>
            </table>
          </div>

          <!-- File List Dialog -->
          <v-dialog v-model="fileDialog" max-width="500px" v-if="fileDialogRowType">
            <v-card>
              <v-card-title class="subtitle-1 font-weight-bold">
                Attachments — {{ fileDialogRowType }}
                <v-spacer />
                <v-btn icon @click="fileDialog = false"><v-icon>mdi-close</v-icon></v-btn>
              </v-card-title>
              <v-card-text>
                <v-list dense v-if="getAttachments(fileDialogRowType).length">
                  <v-list-item v-for="att in getAttachments(fileDialogRowType)" :key="att.id">
                    <v-list-item-content>
                      <v-list-item-title class="caption">{{ att.fileName }}</v-list-item-title>
                      <v-list-item-subtitle class="caption grey--text">
                        {{ Math.round(att.fileSize / 1024) }} KB
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon x-small @click="downloadFile(att)">
                        <v-icon x-small color="primary">mdi-download</v-icon>
                      </v-btn>
                      <v-btn icon x-small @click="deleteAttachment(att)">
                        <v-icon x-small color="error">mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
                <div v-else class="caption grey--text py-4 text-center">No files attached</div>
              </v-card-text>
            </v-card>
          </v-dialog>

          <!-- Save Section C + hidden file input -->
          <div class="mt-2 d-flex align-center" v-if="canEditSectionC">
            <v-btn x-small color="success" @click="saveSectionC"
              >Save Section C</v-btn
            >
            <input
              id="claim-file-input"
              type="file"
              style="display:none"
              @change="handleFileSelected"
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.csv"
            />
          </div>
        </div>

        <!-- Action Buttons & Notes -->
        <v-card flat class="mt-4" v-if="!reportData.frozen">
          <v-card-text>
            <v-row>
              <v-col cols="12" class="d-flex align-center flex-wrap">
                <template
                  v-if="isAdjuster && reportData.adjId === currentUserId"
                >
                  <v-btn
                    v-if="!isLocked"
                    color="success"
                    small
                    @click="finalizeMonth"
                    :disabled="reportData.groupStatus !== 'DRAFT'"
                    >Submit Month</v-btn
                  >
                  <v-btn
                    v-if="reportData.groupStatus === 'QUERIED'"
                    color="primary"
                    small
                    class="ml-2"
                    @click="transitionStatus('MANAGER_REVIEW')"
                    >Submit Reply</v-btn
                  >
                </template>
                <!-- Manager actions -->
                <template
                  v-if="
                    isManager && reportData.groupStatus === 'MANAGER_REVIEW'
                  "
                >
                  <v-btn
                    color="success"
                    small
                    class="mr-1"
                    @click="transitionStatus('MANAGER_APPROVED')"
                    >Approve</v-btn
                  >
                  <v-btn
                    color="warning"
                    small
                    class="mr-1"
                    @click="transitionStatus('QUERIED')"
                    >Query</v-btn
                  >
                  <v-btn
                    color="error"
                    small
                    @click="transitionStatus('REJECTED')"
                    >Reject</v-btn
                  >
                </template>
                <!-- Payroll Clerk: Begin Review (from MANAGER_APPROVED) -->
                <template
                  v-if="
                    isPayrollClerk &&
                    reportData.groupStatus === 'MANAGER_APPROVED'
                  "
                >
                  <v-btn
                    color="primary"
                    small
                    @click="transitionStatus('PAYROLL_CLERK_REVIEW')"
                    >Begin Review</v-btn
                  >
                </template>
                <!-- Payroll Clerk actions -->
                <template
                  v-if="
                    isPayrollClerk &&
                    reportData.groupStatus === 'PAYROLL_CLERK_REVIEW'
                  "
                >
                  <v-btn
                    color="success"
                    small
                    class="mr-1"
                    @click="transitionStatus('PAYROLL_CLERK_APPROVED')"
                    >Verify & Approve</v-btn
                  >
                  <v-btn
                    color="warning"
                    small
                    class="mr-1"
                    @click="transitionStatus('QUERIED')"
                    >Send Back</v-btn
                  >
                  <v-btn
                    color="error"
                    small
                    @click="transitionStatus('REJECTED')"
                    >Reject</v-btn
                  >
                </template>
                <!-- Payroll Manager: Begin Review (from PAYROLL_CLERK_APPROVED) -->
                <template
                  v-if="
                    isPayrollManager &&
                    reportData.groupStatus === 'PAYROLL_CLERK_APPROVED'
                  "
                >
                  <v-btn
                    color="primary"
                    small
                    @click="transitionStatus('PAYROLL_MANAGER_REVIEW')"
                    >Begin Review</v-btn
                  >
                </template>
                <!-- Payroll Manager actions -->
                <template
                  v-if="
                    isPayrollManager &&
                    reportData.groupStatus === 'PAYROLL_MANAGER_REVIEW'
                  "
                >
                  <v-text-field
                    v-model="actionAmount"
                    label="Paid Amount (RM)"
                    dense
                    type="number"
                    class="mr-2"
                    style="max-width: 150px"
                  />
                  <v-btn
                    color="success"
                    small
                    class="mr-1"
                    @click="transitionStatus('PAID')"
                    >Mark as Paid</v-btn
                  >
                  <v-btn
                    color="warning"
                    small
                    class="mr-1"
                    @click="transitionStatus('QUERIED')"
                    >Send Back</v-btn
                  >
                  <v-btn
                    color="error"
                    small
                    @click="transitionStatus('REJECTED')"
                    >Reject</v-btn
                  >
                </template>
                <!-- Query/Reject notifications via snackbar are handled in transitionStatus -->
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Communication Notes -->
        <v-card flat class="mt-2">
          <v-card-text>
            <div class="font-weight-bold mb-2">Communication Notes</div>
            <div style="max-height: 200px; overflow-y: auto" class="mb-2">
              <div v-for="note in notes" :key="note.id" class="mb-1">
                <strong>{{ note.author?.username || "User" }}</strong>
                <span class="caption grey--text ml-2">{{
                  formatDateTime(note.createdAt)
                }}</span>
                <p class="ma-0 body-2">{{ note.message }}</p>
                <v-divider class="mt-1" />
              </div>
              <div v-if="notes.length === 0" class="caption grey--text">
                No notes yet
              </div>
            </div>
            <v-text-field
              v-model="newNote"
              label="Add a note..."
              dense
              hide-details
              @keydown.enter="submitNote"
            >
              <template v-slot:append-outer>
                <v-btn icon small color="primary" @click="submitNote"
                  ><v-icon>mdi-send</v-icon></v-btn
                >
              </template>
            </v-text-field>
          </v-card-text>
        </v-card>

        <!-- Claim Action Dialog (opens when clicking a Section A row) -->
        <v-dialog v-model="claimDialog" max-width="600px" persistent scrollable>
          <v-card v-if="selectedClaim" tile>
            <v-card-title class="headline">
              Case #{{ selectedClaim.caseId || selectedClaim.casefile?.id }}
              <v-spacer />
              <v-chip small color="primary"
                >RM {{ claimTotalNum(selectedClaim).toFixed(2) }}</v-chip
              >
              <v-btn icon @click="claimDialog = false"
                ><v-icon>mdi-close</v-icon></v-btn
              >
            </v-card-title>
            <v-card-text class="pt-4">
              <!-- Invoice Info + Adjusting Fees (top section, read-only) -->
              <v-row dense class="mb-2">
                <v-col cols="4">
                  <div class="caption grey--text">Invoice No</div>
                  <div class="body-2 font-weight-medium">
                    {{
                      selectedClaim.invoiceNo ||
                      selectedClaim.casefile?.invNo ||
                      "—"
                    }}
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="caption grey--text">Invoice Total (RM)</div>
                  <div class="body-2 font-weight-medium">
                    RM
                    {{
                      fmt(
                        selectedClaim.invoiceTotal ||
                          selectedClaim.casefile?.invTotal,
                      )
                    }}
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="caption grey--text">Adj Fees (RM)</div>
                  <div class="body-2 font-weight-medium">
                    RM
                    {{
                      fmt(
                        selectedClaim.feeNote ||
                          selectedClaim.casefile?.invFeeNote,
                      )
                    }}
                  </div>
                </v-col>
              </v-row>

              <v-divider class="mb-4 mt-2" />

              <!-- Financial Fields -->
              <v-row class="mb-4">
                <v-col
                  cols="6"
                  class="pb-1"
                  v-for="field in claimFields"
                  :key="field.key"
                >
                  <v-text-field
                    v-model.number="selectedClaim[field.key]"
                    :label="field.label"
                    dense
                    type="number"
                    step="0.01"
                    hide-details
                    :readonly="!canEditClaim"
                  />
                </v-col>
              </v-row>

              <v-divider class="mb-3" />

              <!-- Claim Notes Timeline -->
              <div class="mt-3">
                <div class="font-weight-bold subtitle-2 mb-1">Notes</div>
                <div style="max-height: 200px; overflow-y: auto" class="mb-2">
                  <div v-for="note in claimNotes" :key="note.id" class="mb-1">
                    <strong>{{ note.author?.username || "User" }}</strong>
                    <span class="caption grey--text ml-2">{{
                      formatDateTime(note.createdAt)
                    }}</span>
                    <p class="ma-0 body-2">{{ note.message }}</p>
                    <v-divider class="mt-1" />
                  </div>
                  <div
                    v-if="claimNotes.length === 0"
                    class="caption grey--text"
                  >
                    No notes for this claim
                  </div>
                </div>
              </div>

              <v-text-field
                v-model="claimComment"
                label="Add a comment..."
                dense
                hide-details
                @keydown.enter="sendClaimComment"
              >
                <template v-slot:append-outer>
                  <v-btn
                    icon
                    x-small
                    color="primary"
                    @click="sendClaimComment"
                    :disabled="!claimComment.trim()"
                    ><v-icon>mdi-send</v-icon></v-btn
                  >
                </template>
              </v-text-field>
            </v-card-text>
            <v-card-actions>
              <v-btn
                color="primary"
                text
                @click="saveClaimChanges"
                :disabled="!canEditClaim"
                >Save Changes</v-btn
              >
              <v-btn
                color="info"
                text
                @click="sendClaimComment"
                :disabled="!claimComment.trim()"
                >Send Comment</v-btn
              >
              <v-spacer />
              <v-btn text @click="claimDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center mt-12">
        <v-icon size="64" color="grey lighten-1"
          >mdi-file-document-outline</v-icon
        >
        <div class="text-h6 grey--text mt-2">
          {{
            isManager || isBranchManager
              ? "Select an adjuster and click Submit to view the report"
              : "No report data available"
          }}
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import ClaimMonthlyDataService from "@/services/ClaimMonthlyDataService";
import DeptDataService from "@/services/DeptDataService";
import SubDeptDataService from "@/services/SubDeptDataService";
import InsDataService from "@/services/InsDataService";
import moment from "moment";

export default {
  name: "MonthlyClaimsDashboard",
  data() {
    return {
      // Filtering
      branches: [],
      filterBranchId: null,
      filterAdjId: null,
      allAdjusters: [],
      filteredAdjusters: [],
      // Report data
      loadingReport: false,
      reportData: null,
      adjusterName: "",
      reportMonth: "",
      sectionBRows: [],
      sectionCRows: [],
      notes: [],
      newNote: "",
      actionComment: "",
      actionAmount: null,
      // File uploads for Section C
      attachments: {}, // rowType -> array of attachment objects
      uploadingRowType: null,
      fileDialog: false,
      fileDialogRowType: null,
      // Reference data (loaded like Home.vue)
      refType: [],
      subRefTypes: [],
      insurer: [],
      // Claim action dialog
      claimDialog: false,
      selectedClaim: null,
      claimComment: "",
      claimNotes: [],
      // Phase 4a features
      primaryRole: "",
      pendingSections: [],
      isLocked: false,
      tabIndex: 0, // 0 = Active Claims, 1 = Current Month
      filterMonth: null,
      specifyOptions: [
        "ACCOMODATION",
        "CAR RENTAL",
        "CLEANER SERVICES",
        "COURRIER SERVICE",
        "FLIGHT TICKET",
        "INTERNET BILL",
        "MARKETING EXPENSE",
        "MONTHLY PARKING FEES",
        "STATIONERIES",
        "TOLL",
        "UP KEEP COMPUTER",
        "UP KEEP OFFICE",
        "MISC",
      ],
      claimFields: [
        { key: "travMileage", label: "Travelling / Mileage" },
        { key: "additional", label: "Additional Travel" },
        { key: "sdAndStamps", label: "SD & Stamps" },
        { key: "medicalReport", label: "Medical Report" },
        { key: "policeDoc", label: "Police Document" },
        { key: "jpjDoc", label: "JPJ Document" },
        { key: "misc", label: "Miscellaneous" },
        { key: "typing", label: "Typing" },
      ],
    };
  },
  computed: {
    currentUserId() {
      return this.$store.state.auth.user?.id;
    },
    isAdmin() {
      return this.primaryRole
        ? this.primaryRole === "admin" || this.primaryRole === "super_admin"
        : this.hasRole("admin");
    },
    isManager() {
      return this.primaryRole
        ? this.primaryRole === "manager" || this.primaryRole === "branchmanager"
        : this.hasRole("manager") || this.hasRole("branchmanager");
    },
    isBranchManager() {
      return this.primaryRole
        ? this.primaryRole === "branchmanager"
        : this.hasRole("branchmanager");
    },
    isPayrollManager() {
      return this.primaryRole
        ? this.primaryRole === "payroll_manager"
        : this.hasRole("payroll_manager");
    },
    isPayrollClerk() {
      return this.primaryRole
        ? this.primaryRole === "payroll_clerk"
        : this.hasRole("payroll_clerk");
    },
    isAdjuster() {
      return this.primaryRole
        ? this.primaryRole === "adjuster"
        : this.hasRole("adjuster");
    },
    userRoles() {
      const user = this.$store.state.auth.user;
      if (!user?.roles) return [];
      const map = {
        adjuster: "ADJUSTER",
        manager: "MANAGER",
        branchmanager: "BRANCH MANAGER",
        payroll_clerk: "PAYROLL CLERK",
        payroll_manager: "PAYROLL MANAGER",
        admin: "ADMIN",
        super_admin: "SUPER ADMIN",
      };
      const roles = [];
      user.roles.forEach((r) => {
        const code = (r.roleCode || r).toLowerCase();
        if (map[code]) roles.push({ value: code, text: map[code] });
      });
      return roles.sort((a, b) => a.text.localeCompare(b.text));
    },
    currentMonth() {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
    },
    previousMonth() {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const prevYear =
        now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      return `${prevYear}-${pad(prevMonth + 1)}`;
    },
    // Tab 0 = Active Claims, Tab 1 = Current Month
    filteredPendingSections() {
      if (this.tabIndex === 1) {
        // Current Month: PRE-DRAFT and DRAFT for current month
        return this.pendingSections.filter((s) =>
          ["PRE-DRAFT", "DRAFT"].includes(s.status),
        );
      }
      // Active Claims: all non-closed statuses
      return this.pendingSections.filter((s) =>
        [
          "PRE-DRAFT",
          "DRAFT",
          "MANAGER_REVIEW",
          "MANAGER_APPROVED",
          "PAYROLL_CLERK_REVIEW",
          "PAYROLL_CLERK_APPROVED",
          "PAYROLL_MANAGER_REVIEW",
          "PAID",
          "QUERIED",
          "REJECTED",
          "PENDING",
        ].includes(s.status),
      );
    },
    // Tab 0 = Active Claims, Tab 1 = Current Month
    filteredSectionAClaims() {
      if (!this.reportData?.sectionAClaims) return [];
      if (this.tabIndex === 1) {
        // Current Month: PRE-DRAFT and DRAFT
        return this.reportData.sectionAClaims.filter((c) =>
          ["PRE-DRAFT", "DRAFT"].includes(
            c.groupStatus || c.status || "DRAFT",
          ),
        );
      }
      // Active Claims: all non-closed statuses
      return this.reportData.sectionAClaims.filter((c) =>
        [
          "PRE-DRAFT",
          "DRAFT",
          "MANAGER_REVIEW",
          "MANAGER_APPROVED",
          "PAYROLL_CLERK_REVIEW",
          "PAYROLL_CLERK_APPROVED",
          "PAYROLL_MANAGER_REVIEW",
          "PAID",
          "QUERIED",
          "REJECTED",
          "PENDING",
        ].includes(c.groupStatus || c.status || "DRAFT"),
      );
    },
    specifyRows() {
      // Show all specify rows (including empty ones so user can fill them in)
      return (this.sectionCRows || []).filter(r => r.rowType && r.rowType.startsWith('specify'));
    },
    totalSpecifyCount() {
      // Count ALL specify rows (including empty) for the limit check
      return (this.sectionCRows || []).filter(r => r.rowType && r.rowType.startsWith('specify')).length;
    },
    canEditReport() {
      if (!this.reportData) return false;
      const isOwner =
        this.isAdjuster && this.reportData.adjId === this.currentUserId;
      const isReviewer =
        this.isManager ||
        this.isPayrollClerk ||
        this.isPayrollManager ||
        this.isAdmin;
      return isOwner || isReviewer;
    },
    subTotal() {
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += this.claimTotalNum(c);
      });
      this.sectionBRows.forEach((r) => {
        total += this.rowTotalNum(r);
      });
      return total;
    },
    amountPayable() {
      const rows = this.sectionCRows || [];
      const accom = parseFloat(rows.find(r => r.rowType === 'accomodation')?.amount || 0);
      const phone = parseFloat(rows.find(r => r.rowType === 'handphone')?.amount || 0);
      const parking = parseFloat(rows.find(r => r.rowType === 'parking')?.amount || 0);
      let specify = 0;
      rows.filter(r => r.rowType && r.rowType.startsWith('specify')).forEach(r => {
        specify += parseFloat(r.amount || 0);
      });
      const advance = parseFloat(rows.find(r => r.rowType === 'advance')?.amount || 0);
      return Math.max(
        0,
        this.subTotal + accom + phone + parking + specify - advance,
      );
    },
    grandTotalAll() {
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += this.claimTotalNum(c);
      });
      this.sectionBRows.forEach((r) => {
        total += this.rowTotalNum(r);
      });
      return total;
    },
    statusMessage() {
      const s = this.reportData?.groupStatus;
      const msgs = {
        "PRE-DRAFT": "PRE-DRAFT — Awaiting activation",
        DRAFT: "DRAFT — Not yet submitted",
        MANAGER_REVIEW: "Pending Manager Approval",
        MANAGER_APPROVED:
          "Approved by Manager — Forwarded to Payroll Clerk",
        PAYROLL_CLERK_REVIEW: "Pending Payroll Clerk Review",
        PAYROLL_CLERK_APPROVED:
          "Approved by Payroll Clerk — Forwarded to Payroll Manager",
        PAYROLL_MANAGER_REVIEW: "Pending Payroll Manager Review",
        PAID: "PAID — Claim Completed",
        QUERIED: "Queried — Awaiting Response",
        REJECTED: "Rejected",
        PENDING: "Pending — Carried Forward",
      };
      return msgs[s] || s || "No status";
    },
canEditClaim() {
      if (!this.selectedClaim) return false;
      const s = this.reportData?.groupStatus;
      const isOwner =
        this.isAdjuster && this.reportData?.adjId === this.currentUserId;
      // PRE-DRAFT (current month): always editable by owner, no lock
      if (isOwner && s === "PRE-DRAFT") return true;
      // DRAFT/QUERIED (previous months): editable by owner unless locked
      if (isOwner && (s === "DRAFT" || s === "QUERIED")) return !this.isLocked;
      if (this.isManager && s === "MANAGER_REVIEW") return true;
      if (this.isPayrollClerk && s === "PAYROLL_CLERK_REVIEW") return true;
      if (
        this.isPayrollManager &&
        (s === "PAYROLL_MANAGER_REVIEW" || s === "PAYROLL_CLERK_REVIEW")
      )
        return true;
      return false;
    },
    canEditSectionB() {
      if (!this.reportData?.groupStatus) return false;
      const s = this.reportData.groupStatus;
      // PRE-DRAFT (current month): always editable by owner adjuster
      if (s === "PRE-DRAFT" && this.isAdjuster && this.reportData.adjId === this.currentUserId) {
        return true;
      }
      if (
        s === "DRAFT" &&
        this.isAdjuster &&
        this.reportData.adjId === this.currentUserId
      ) {
        return !this.isLocked;
      }
      if (s === "MANAGER_REVIEW" && (this.isManager || this.isBranchManager))
        return true;
      if (
        s === "PAYROLL_CLERK_REVIEW" &&
        (this.isPayrollClerk || this.isPayrollManager)
      )
        return true;
      if (s === "PAYROLL_MANAGER_REVIEW" && this.isPayrollManager) return true;
      return false;
    },
    canEditSectionC() {
      if (!this.reportData?.groupStatus) return false;
      const s = this.reportData.groupStatus;
      // PRE-DRAFT (current month): always editable by owner adjuster
      if (s === "PRE-DRAFT" && this.isAdjuster && this.reportData.adjId === this.currentUserId) {
        return true;
      }
      if (
        s === "DRAFT" &&
        this.isAdjuster &&
        this.reportData.adjId === this.currentUserId
      ) {
        return !this.isLocked;
      }
      if (s === "MANAGER_REVIEW" && (this.isManager || this.isBranchManager))
        return true;
      if (
        s === "PAYROLL_CLERK_REVIEW" &&
        (this.isPayrollClerk || this.isPayrollManager)
      )
        return true;
      if (s === "PAYROLL_MANAGER_REVIEW" && this.isPayrollManager) return true;
      return false;
    },
  },
  watch: {
    tabIndex(val) {
      const mode = val === 0 ? "active" : "current";
      this.loadPendingSummary();
      if (this.filterAdjId) {
        this.loadReport(mode);
      } else if (this.isAdjuster) {
        this.filterAdjId = this.currentUserId;
        this.loadReport(mode);
      }
    },
  },
  methods: {
    hasRole(code) {
      const user = this.$store.state.auth.user;
      if (!user || !user.roles) return false;
      return user.roles.some(
        (r) => (r.roleCode || r).toLowerCase() === code,
      );
    },
    // ─── Filter methods ────────────────────────────────────────────────
    loadBranches() {
      let branches = this.$store.state.referenceData.branches || [];
      const user = this.$store.state.auth.user || {};
      const roles = Array.isArray(user.roles) ? user.roles : [];
      const isAdminOrManager =
        roles.includes("ADMIN") || roles.includes("MANAGER");
      if (!isAdminOrManager) {
        const userBranches = Array.isArray(user.branches)
          ? user.branches
          : [];
        branches = branches.filter((x) => userBranches.includes(x.id));
      }
      if (branches?.length) {
        this.branches = branches
          .map((b) => ({ ...b, name: b.name?.toUpperCase() || "" }))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (this.isBranchManager && branches.length === 1) {
          this.filterBranchId = branches[0].id;
          this.loadAdjusters(this.filterBranchId);
        }
      }
    },
    async loadAdjusters(branchId) {
      try {
        const params = {};
        if (branchId) params.branchId = branchId;
        const res = await ClaimMonthlyDataService.getAdjusters(params);
        this.allAdjusters = (res.data || [])
          .map((a) => ({ ...a, username: a.username?.toUpperCase() || "" }))
          .sort((a, b) => a.username.localeCompare(b.username));
        this.filteredAdjusters = branchId ? this.allAdjusters : [];
      } catch (e) {
        console.error("Failed to load adjusters", e);
        this.allAdjusters = [];
        this.filteredAdjusters = [];
      }
    },
    onBranchChange(branchId) {
      this.filterAdjId = null;
      this.filteredAdjusters = [];
      if (branchId) this.loadAdjusters(branchId);
    },
    setTab(index) {
      // Native click handler — sets tab index and triggers data load
      this.tabIndex = index;
      const mode = index === 0 ? "active" : "current";
      this.loadPendingSummary();
      if (this.filterAdjId) {
        this.loadReport(mode);
      } else if (this.isAdjuster) {
        this.filterAdjId = this.currentUserId;
        this.loadReport(mode);
      }
    },
    clearReport() {
      this.filterBranchId = null;
      this.filterAdjId = null;
      this.filteredAdjusters = [];
      this.reportData = null;
      this.sectionBRows = [];
      this.sectionCRows = [];
      this.notes = [];
      this.actionComment = "";
      this.actionAmount = null;
    },
    // ─── Load Report ────────────────────────────────────────────────────
    async loadReport(viewModeOverride) {
      const adjId = this.filterAdjId || this.currentUserId;
      if (!adjId) return;
      this.loadingReport = true;
      this.reportData = null;
      try {
        // Determine month based on view mode
        // "current-month" sentinel → backend treats as current-month PRE-DRAFT view
        // Use viewModeOverride if provided, otherwise derive from tabIndex
        const currentViewMode = viewModeOverride || (this.tabIndex === 0 ? "active" : "current");
        let monthParam = this.previousMonth;
        if (currentViewMode === "current") {
          monthParam = "current-month";
        }
        const res = await ClaimMonthlyDataService.getAdjusterReport({
          adjId,
          month: monthParam,
        });
        this.reportData = res.data;
        const adj = this.allAdjusters.find((a) => a.id === adjId);
        this.adjusterName = adj?.username || `User #${adjId}`;
        const monthStr = this.reportData.month || "";
        if (monthStr) {
          const [y, m] = monthStr.split("-");
          const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December",
          ];
          this.reportMonth = `${months[parseInt(m) - 1] || ""} ${y}`;
        } else {
          this.reportMonth = moment()
            .subtract(1, "month")
            .format("MMMM YYYY");
        }
        this.sectionBRows =
          this.reportData.sectionBRows?.length > 0
            ? this.reportData.sectionBRows.map((r) => ({ ...r }))
            : [];
        this.sectionCRows =
          this.reportData.sectionCRows?.length > 0
            ? this.reportData.sectionCRows.map((r) => ({ ...r }))
            : [
                { rowType: "accomodation", rate: 80, days: 0, amount: 0 },
                { rowType: "handphone", amount: 0 },
                { rowType: "parking", amount: 0 },
                { rowType: "specify_1", label: "", amount: 0 },
                { rowType: "advance", amount: 0 },
              ];
        if (this.reportData.monthlyGroupId) {
          const notesRes = await ClaimMonthlyDataService.getNotes(
            this.reportData.monthlyGroupId,
          );
          this.notes = notesRes.data || [];
        } else {
          this.notes = [];
        }
        // Load file attachments for Section C
        this.loadAttachments();
        // Check adjuster lock
        this.isLocked = false;
        if (this.isAdjuster) {
          try {
            const settingsRes = await import("@/services/api").then((m) =>
              m.default.get("/settings/management"),
            );
            const claimModule = settingsRes.data?.claimModule || {};
            let lockDay = 5;
            if (claimModule.jobs?.["adjuster-lock"]?.day) {
              lockDay = parseInt(claimModule.jobs["adjuster-lock"].day);
            } else if (claimModule.adjusterLockDay) {
              lockDay = parseInt(claimModule.adjusterLockDay);
            }
            const today = new Date().getDate();
            if (today > lockDay && this.reportData.month) {
              const [y, m] = this.reportData.month.split("-");
              const reportDate = new Date(parseInt(y), parseInt(m) - 1);
              const now = new Date();
              if (
                reportDate.getFullYear() < now.getFullYear() ||
                reportDate.getMonth() < now.getMonth()
              ) {
                this.isLocked = true;
              }
            }
          } catch (e) {
            // non-blocking
          }
        }
        this.$nextTick(() => {
          this.actionAmount = this.amountPayable || null;
        });
      } catch (e) {
        console.error(
          "Failed to load report",
          e.response?.data || e.message,
        );
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text:
            "Failed to load report: " +
            (e.response?.data?.message || e.message),
          color: "error",
        });
      } finally {
        this.loadingReport = false;
      }
      this.loadPendingSummary();
    },
    async loadPendingSummary() {
      try {
        // Determine month based on view mode
        let targetMonth = this.filterMonth || this.previousMonth;
        if (this.tabIndex === 1) {
          targetMonth = "current-month";
        }
        const res = await ClaimMonthlyDataService.pendingSummary({
          month: targetMonth,
        });
        this.pendingSections = res.data || [];
      } catch (e) {
        this.pendingSections = [];
      }
    },
    quickSelectAdjuster(adj) {
      this.filterAdjId = adj.adjId;
      this.loadReport();
    },
    // ─── Section B methods ──────────────────────────────────────────────
    calcAccomTotal() {
      const row = this.sectionCRow('accomodation');
      if (row) {
        row.amount = parseFloat(row.rate || 0) * parseInt(row.days || 0);
      }
    },
    addSectionBRow() {
      this.sectionBRows.push({
        type: "",
        fileNo: "",
        vehicleNo: "",
        insurer: "",
        invoiceNo: "",
        invAmount: 0,
        adjFees: 0,
        placeOfLoss: "",
        travMileage: 0,
        additional: 0,
        sdAndStamps: 0,
        medicalReport: 0,
        policeDoc: 0,
        jpjDoc: 0,
        misc: 0,
        typing: 0,
      });
    },
    removeSectionBRow(index) {
      this.sectionBRows.splice(index, 1);
    },
    // ─── Dynamic Specify Rows ─────────────────────────────────────────────
    addSpecifyRow() {
      if (this.totalSpecifyCount >= 50) return;
      this.sectionCRows.push({ rowType: `specify_${Date.now()}`, label: '', amount: 0 });
    },
    removeSpecifyRow(index) {
      // Find the actual index in sectionCRows (not the filtered specifyRows index)
      const specifyIndices = this.sectionCRows
        .map((r, i) => r.rowType && r.rowType.startsWith('specify') ? i : -1)
        .filter(i => i >= 0);
      const actualIdx = specifyIndices[index];
      if (actualIdx !== undefined) this.sectionCRows.splice(actualIdx, 1);
    },
    async lookupCasefile(row) {
      if (!row.fileNo || !/^\d+$/.test(row.fileNo)) return;
      try {
        const CasefileDataService = await import(
          "@/services/CasefileDataService"
        );
        const res = await CasefileDataService.default.get(row.fileNo);
        const cf = res.data;
        if (!cf) return;
        row.vehicleNo =
          cf.vehicleNo?.toUpperCase() || row.vehicleNo || "";
        row.placeOfLoss = cf.placeOfLoss || row.placeOfLoss || "";
        row.invoiceNo = cf.invNo || row.invoiceNo || "";
        row.invAmount = cf.invTotal || row.invAmount || 0;
        row.adjFees = cf.invFeeNote || row.adjFees || 0;
        const depts =
          this.$store.state.referenceData.departments || [];
        const dept = depts.find(
          (d) => Number(d.id) === Number(cf.refType),
        );
        const sub = this.subRefTypes.find(
          (s) => Number(s.id) === Number(cf.subRefType),
        );
        row.type = sub?.subCode
          ? `${dept?.name || ""}/${sub.subCode}`
          : dept?.name || "";
        const ins = this.insurer.find(
          (i) => Number(i.id) === Number(cf.insurer),
        );
        row.insurer = ins?.insCode || ins?.name || cf.insurer || "";
      } catch (e) {
        console.log("Casefile lookup failed:", row.fileNo);
      }
    },
    async saveSectionB() {
      if (!this.reportData?.monthlyGroupId) {
        console.warn("saveSectionB: no monthlyGroupId");
        return;
      }
      try {
        await ClaimMonthlyDataService.updateSectionB(
          this.reportData.monthlyGroupId,
          this.sectionBRows,
        );
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: "Section B saved",
          color: "success",
        });
      } catch (e) {
        console.error(
          "Save Section B failed:",
          e.response?.data || e.message,
        );
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text:
            "Failed to save Section B: " +
            (e.response?.data?.message || e.message),
          color: "error",
        });
      }
    },
    async saveSectionC() {
      if (!this.reportData?.monthlyGroupId) {
        console.warn("saveSectionC: no monthlyGroupId");
        return;
      }
      try {
        await ClaimMonthlyDataService.updateSectionC(
          this.reportData.monthlyGroupId,
          this.sectionCRows,
        );
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: "Section C saved",
          color: "success",
        });
      } catch (e) {
        console.error(
          "Save Section C failed:",
          e.response?.data || e.message,
        );
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text:
            "Failed to save Section C: " +
            (e.response?.data?.message || e.message),
          color: "error",
        });
      }
    },
    // ─── File Uploads for Section C ──────────────────────────────────────
    triggerUpload(rowType) {
      if (!this.reportData?.monthlyGroupId) return;
      this.uploadingRowType = rowType;
      this.$nextTick(() => {
        const input = this.$el.querySelector('#claim-file-input');
        if (input) input.click();
      });
    },
    async handleFileSelected(e) {
      const file = e.target?.files?.[0];
      if (!file || !this.uploadingRowType || !this.reportData?.monthlyGroupId) return;
      const groupId = this.reportData.monthlyGroupId;
      const rowType = this.uploadingRowType;
      this.uploadingRowType = null;
      e.target.value = '';
      const formData = new FormData();
      formData.append('file', file);
      formData.append('rowType', rowType);
      try {
        const { default: ClaimMonthlyDataService } = await import('@/services/ClaimMonthlyDataService');
        const res = await ClaimMonthlyDataService.uploadAttachment(groupId, formData);
        const att = res.data?.attachment;
        if (att) {
          if (!this.attachments[rowType]) this.attachments[rowType] = [];
          this.attachments[rowType].push(att);
        }
        this.$store.dispatch('setSnackbar', { showing: true, text: 'File uploaded', color: 'success' });
      } catch (e) {
        this.$store.dispatch('setSnackbar', { showing: true, text: e.response?.data?.message || 'Upload failed', color: 'error' });
      }
    },
    async loadAttachments() {
      if (!this.reportData?.monthlyGroupId) return;
      try {
        const { default: ClaimMonthlyDataService } = await import('@/services/ClaimMonthlyDataService');
        const res = await ClaimMonthlyDataService.getAttachments(this.reportData.monthlyGroupId);
        const list = res.data || [];
        const map = {};
        list.forEach(a => {
          if (!map[a.rowType]) map[a.rowType] = [];
          map[a.rowType].push(a);
        });
        this.attachments = map;
      } catch (e) { /* non-blocking */ }
    },
    getAttachments(rowType) {
      return this.attachments[rowType] || [];
    },
    // Safely find a Section C row by rowType (avoids hardcoded index issues)
    sectionCRow(rowType) {
      return (this.sectionCRows || []).find(r => r.rowType === rowType) || { amount: 0, rate: 80, days: 0, label: '' };
    },
    showFiles(rowType) {
      this.fileDialogRowType = rowType;
      this.fileDialog = true;
    },
    async downloadFile(att) {
      try {
        const { default: ClaimMonthlyDataService } = await import('@/services/ClaimMonthlyDataService');
        const res = await ClaimMonthlyDataService.downloadAttachment(att.id);
        // Create a blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = att.fileName || `attachment-${att.id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$store.dispatch('setSnackbar', { showing: true, text: 'Download failed', color: 'error' });
      }
    },
    async deleteAttachment(att) {
      if (!confirm('Delete this file?')) return;
      try {
        const { default: ClaimMonthlyDataService } = await import('@/services/ClaimMonthlyDataService');
        await ClaimMonthlyDataService.deleteAttachment(att.id);
        const list = this.attachments[att.rowType] || [];
        this.attachments[att.rowType] = list.filter(a => a.id !== att.id);
        this.$store.dispatch('setSnackbar', { showing: true, text: 'File deleted', color: 'info' });
      } catch (e) {
        this.$store.dispatch('setSnackbar', { showing: true, text: 'Delete failed', color: 'error' });
      }
    },
    // ─── Status & Finalize ──────────────────────────────────────────────
    async finalizeMonth() {
      if (!this.reportData?.monthlyGroupId) return;
      try {
        await ClaimMonthlyDataService.updateStatus(
          this.reportData.monthlyGroupId,
          {
            status: "MANAGER_REVIEW",
            comment:
              this.actionComment || "Adjuster submitted month for review",
          },
        );
        this.reportData.groupStatus = "MANAGER_REVIEW";
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: "Month submitted for manager review",
          color: "success",
        });
        this.loadPendingSummary();
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: e.response?.data?.message || "Failed to submit",
          color: "error",
        });
      }
    },
    async transitionStatus(status) {
      if (!this.reportData?.monthlyGroupId) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: "No active monthly group",
          color: "warning",
        });
        return;
      }
      try {
        await ClaimMonthlyDataService.updateStatus(
          this.reportData.monthlyGroupId,
          {
            status,
            comment: this.actionComment || undefined,
            amount: this.actionAmount || undefined,
          },
        );
        if (this.actionComment?.trim()) {
          await ClaimMonthlyDataService.addNote(
            this.reportData.monthlyGroupId,
            this.actionComment,
          );
          this.actionComment = "";
        }
        const statusLabels = {
          MANAGER_APPROVED: "Approved — forwarded to Payroll Clerk",
          QUERIED: "Claim queried — adjuster notified",
          REJECTED: "Claim rejected",
          PAYROLL_CLERK_APPROVED:
            "Payroll Clerk approved — forwarded to Payroll Manager",
          PAID: "Claim marked as PAID",
          MANAGER_REVIEW: "Reply submitted",
          PAYROLL_CLERK_REVIEW: "Review started",
          PAYROLL_MANAGER_REVIEW: "Review started",
        };
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text:
            statusLabels[status] || `Status changed to ${status}`,
          color: "success",
        });
        this.loadPendingSummary();
        this.loadReport();
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: e.response?.data?.message || "Status update failed",
          color: "error",
        });
      }
    },
    async submitNote() {
      if (!this.newNote.trim() || !this.reportData?.monthlyGroupId) return;
      try {
        const res = await ClaimMonthlyDataService.addNote(
          this.reportData.monthlyGroupId,
          this.newNote,
        );
        this.notes.push(res.data);
        this.newNote = "";
      } catch (e) {
        console.error("Failed to add note", e);
      }
    },
    // ─── Computations (template helpers) ────────────────────────────────
    claimTotal(claim) {
      return "MYR " + this.claimTotalNum(claim).toFixed(2);
    },
    claimTotalNum(claim) {
      if (!claim) return 0;
      return (
        parseFloat(claim.travMileage || 0) +
        parseFloat(claim.additional || 0) +
        parseFloat(claim.sdAndStamps || 0) +
        parseFloat(claim.medicalReport || 0) +
        parseFloat(claim.policeDoc || 0) +
        parseFloat(claim.jpjDoc || 0) +
        parseFloat(claim.misc || 0) +
        parseFloat(claim.typing || 0)
      );
    },
    rowTotal(row) {
      if (!row) return "0.00";
      return (
        parseFloat(row.travMileage || 0) +
        parseFloat(row.additional || 0) +
        parseFloat(row.sdAndStamps || 0) +
        parseFloat(row.medicalReport || 0) +
        parseFloat(row.policeDoc || 0) +
        parseFloat(row.jpjDoc || 0) +
        parseFloat(row.misc || 0) +
        parseFloat(row.typing || 0)
      ).toFixed(2);
    },
    rowTotalNum(row) {
      if (!row) return 0;
      return (
        parseFloat(row.travMileage || 0) +
        parseFloat(row.additional || 0) +
        parseFloat(row.sdAndStamps || 0) +
        parseFloat(row.medicalReport || 0) +
        parseFloat(row.policeDoc || 0) +
        parseFloat(row.jpjDoc || 0) +
        parseFloat(row.misc || 0) +
        parseFloat(row.typing || 0)
      );
    },
    grandTotalCol(colIndex) {
      const fields = [
        "travMileage",
        "additional",
        "sdAndStamps",
        "medicalReport",
        "policeDoc",
        "jpjDoc",
        "misc",
        "typing",
      ];
      const field = fields[colIndex - 10];
      if (!field) return 0;
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += parseFloat(c[field] || 0);
      });
      this.sectionBRows.forEach((r) => {
        total += parseFloat(r[field] || 0);
      });
      return total;
    },
    sectionASum(colIndex) {
      const fields = [
        "travMileage",
        "additional",
        "sdAndStamps",
        "medicalReport",
        "policeDoc",
        "jpjDoc",
        "misc",
        "typing",
      ];
      const field = fields[colIndex - 10];
      if (!field) return 0;
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += parseFloat(c[field] || 0);
      });
      return total;
    },
    invAmtSum() {
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += parseFloat(
          c.invoiceTotal || c.casefile?.invTotal || 0,
        );
      });
      return total;
    },
    adjFeesSum() {
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += parseFloat(c.feeNote || c.casefile?.invFeeNote || 0);
      });
      return total;
    },
    sectionATotalSum() {
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += this.claimTotalNum(c);
      });
      return total;
    },
    grandInvAmtSum() {
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += parseFloat(
          c.invoiceTotal || c.casefile?.invTotal || 0,
        );
      });
      (this.sectionBRows || []).forEach((r) => {
        total += parseFloat(r.invAmount || 0);
      });
      return total;
    },
    grandAdjFeesSum() {
      let total = 0;
      (this.reportData?.sectionAClaims || []).forEach((c) => {
        total += parseFloat(c.feeNote || c.casefile?.invFeeNote || 0);
      });
      (this.sectionBRows || []).forEach((r) => {
        total += parseFloat(r.adjFees || 0);
      });
      return total;
    },
    // ─── Formatting ────────────────────────────────────────────────────
    fmt(value) {
      return parseFloat(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    statusLabel(status) {
      const labels = {
        DRAFT: "Draft",
        MANAGER_REVIEW: "Manager Review",
        MANAGER_APPROVED: "Manager Approved",
        PAYROLL_CLERK_REVIEW: "Clerk Review",
        PAYROLL_CLERK_APPROVED: "Clerk Approved",
        PAYROLL_MANAGER_REVIEW: "Payroll Review",
        PAID: "Paid",
        QUERIED: "Queried",
        REJECTED: "Rejected",
      };
      return labels[status] || status;
    },
    statusColor(status) {
      const colors = {
        DRAFT: "grey",
        MANAGER_REVIEW: "orange",
        MANAGER_APPROVED: "blue",
        PAYROLL_CLERK_REVIEW: "purple",
        PAYROLL_CLERK_APPROVED: "indigo",
        PAYROLL_MANAGER_REVIEW: "deep-purple",
        PAID: "green",
        QUERIED: "warning",
        REJECTED: "error",
      };
      return colors[status] || "grey";
    },
    pendingChipColor(status) {
      const colors = {
        "PRE-DRAFT": "light-blue",
        DRAFT: "grey",
        MANAGER_REVIEW: "orange",
        MANAGER_APPROVED: "blue",
        PAYROLL_CLERK_REVIEW: "purple",
        PAYROLL_CLERK_APPROVED: "indigo",
        PAYROLL_MANAGER_REVIEW: "deep-purple",
        PAID: "teal",
        QUERIED: "amber darken-2",
        REJECTED: "red",
      };
      return colors[status] || "grey";
    },
    formatDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "";
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY h:mm A") : "";
    },
    getDeptSubRef(claim) {
      if (!claim?.casefile) return "";
      const cf = claim.casefile;
      const dept = this.filterDept(cf.refType)[0];
      const sub = this.filterSubDept(cf.subRefType)[0];
      return sub?.subCode
        ? `${dept?.name || ""}/${sub.subCode}`
        : dept?.name || "";
    },
    getInsurerCode(claim) {
      if (!claim?.casefile) return "";
      const ins = this.filterIns(
        claim.casefile.insurer || claim.casefile.inssId,
      )[0];
      return ins?.insCode || ins?.name || "";
    },
    filterDept(id) {
      return this.refType.filter((d) => d.id === id);
    },
    filterSubDept(id) {
      return this.subRefTypes.filter((s) => s.id === id);
    },
    filterIns(id) {
      return this.insurer.filter((i) => i.id === id);
    },
    isDuplicateClaim(claim) {
      if (!this.reportData?.sectionAClaims) return false;
      const caseId = claim.caseId || claim.casefile?.id;
      if (!caseId) return false;
      return (
        this.reportData.sectionAClaims.filter(
          (c) => (c.caseId || c.casefile?.id) === caseId,
        ).length > 1
      );
    },
    openClaimAction(claim) {
      this.selectedClaim = { ...claim };
      this.claimComment = "";
      this.claimNotes = [];
      if (claim.casefile)
        this.selectedClaim.casefile = { ...claim.casefile };
      import("@/services/ClaimsV3DataService").then((mod) => {
        mod.default
          .getNotes(claim.id)
          .then((res) => {
            this.claimNotes = res.data || [];
          })
          .catch(() => {
            this.claimNotes = [];
          });
      });
      this.claimDialog = true;
    },
    async saveClaimChanges() {
      if (!this.canEditClaim) return;
      try {
        const updates = {};
        this.claimFields.forEach((f) => {
          const val = parseFloat(this.selectedClaim[f.key]);
          if (!isNaN(val)) updates[f.key] = val;
        });
        await ClaimMonthlyDataService.updateClaim(
          this.selectedClaim.id,
          updates,
        );
        if (this.claimComment.trim()) {
          const notePayload = `[Claim #${this.selectedClaim.id}] ${this.claimComment}`;
          const ClaimsV3DataService = await import(
            "@/services/ClaimsV3DataService"
          );
          await ClaimsV3DataService.default.addNote(
            this.selectedClaim.id,
            notePayload,
          );
          this.claimComment = "";
          const res = await ClaimsV3DataService.default.getNotes(
            this.selectedClaim.id,
          );
          this.claimNotes = res.data || [];
        }
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: "Financial details updated",
          color: "success",
        });
        this.loadReport();
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: e.response?.data?.message || "Update failed",
          color: "error",
        });
      }
    },
    async sendClaimComment() {
      if (!this.claimComment.trim()) return;
      try {
        const notePayload = `[Claim #${this.selectedClaim.id}] ${this.claimComment}`;
        const ClaimsV3DataService = await import(
          "@/services/ClaimsV3DataService"
        );
        await ClaimsV3DataService.default.addNote(
          this.selectedClaim.id,
          notePayload,
        );
        this.claimComment = "";
        const res = await ClaimsV3DataService.default.getNotes(
          this.selectedClaim.id,
        );
        this.claimNotes = res.data || [];
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text: "Comment added",
          color: "success",
        });
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          text:
            e.response?.data?.message || "Failed to add comment",
          color: "error",
        });
      }
    },
    formatAasbRef(claim) {
      if (!claim?.casefile) return "";
      const cf = claim.casefile;
      const depts = this.$store.state.referenceData.departments || [];
      const subs = this.$store.state.referenceData.subRefTypes || [];
      const branches = this.$store.state.referenceData.branches || [];
      const dept = depts.find(
        (d) => Number(d.id) === Number(cf.refType),
      );
      const sub = subs.find(
        (s) => Number(s.id) === Number(cf.subRefType),
      );
      const br = branches.find(
        (b) => Number(b.id) === Number(cf.branch),
      );
      const year = cf.dateOfAssign
        ? moment(cf.dateOfAssign).format("YY")
        : cf.createdAt
          ? moment(cf.createdAt).format("YY")
          : "";
      return `AA/${dept?.name || ""}/${sub?.subCode || ""}/${cf.id}/${year}/${br?.brCode || ""}`;
    },
  },
  async mounted() {
    // Set default primary role to the first available role
    if (this.userRoles.length > 0) this.primaryRole = this.userRoles[0].value;
    else this.primaryRole = "";

    await this.$store.dispatch("referenceData/ensureRefs");
    // Load reference data like Home.vue
    try {
      const [deptRes, subRes, insRes] = await Promise.all([
        DeptDataService.getAll(),
        SubDeptDataService.getAll(),
        InsDataService.getAll(),
      ]);
      this.refType = deptRes.data || [];
      this.subRefTypes = subRes.data || [];
      this.insurer = insRes.data || [];
    } catch (e) {
      console.error("Failed to load reference data", e);
    }
    this.loadBranches();
    this.loadAdjusters();
    this.loadPendingSummary();
    // If adjuster, auto-load their report
    if (this.isAdjuster) {
      this.filterAdjId = this.currentUserId;
      this.loadReport();
    }
  },
};
</script>

<style scoped>
.claim-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  table-layout: fixed;
}
.claim-table th,
.claim-table td {
  border: 1px solid #ccc;
  padding: 3px 5px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.claim-table th {
  font-weight: bold;
  text-align: center;
}
.claim-table tbody tr:hover {
  background: #f5f5f5 !important;
}
.text-right {
  text-align: right !important;
}
.amount-cell {
  min-width: 60px;
}
.peach-bg {
  background-color: #fce4d6;
}
.aasb-ref {
  font-family: monospace;
  font-size: 10px;
  white-space: nowrap;
}
.section-header {
  font-weight: bold;
  font-size: 14px;
  padding: 8px 0 4px;
  border-bottom: 2px solid #333;
  margin-bottom: 4px;
}
.extra-claims-wrapper {
  width: 100%;
  overflow-x: auto;
}
.extra-table {
  width: 100%;
  border-collapse: collapse;
}
.extra-table td {
  border: 1px solid #ccc;
  padding: 3px 5px;
  font-size: 11px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.compact-input {
  font-size: 11px !important;
  margin: 0 !important;
  padding: 0 !important;
}
.compact-input >>> input {
  font-size: 11px !important;
}
.compact-input >>> .v-input__slot {
  margin: 0 !important;
  padding: 0 !important;
  min-height: unset !important;
}
.compact-input >>> .v-text-field__details {
  display: none !important;
}
.compact-input >>> .v-input__control {
  min-height: unset !important;
}
.compact-input >>> .v-input {
  margin: 0 !important;
  padding: 0 !important;
}
.compact-number {
  min-width: 50px;
  width: 100%;
}
.compact-number >>> input {
  text-align: right !important;
  padding: 0 2px !important;
  width: 100% !important;
}
.text-right-select >>> .v-select__selection {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}
.duplicate-row {
  background-color: #ffe0b2 !important;
}
.duplicate-row:hover {
  background-color: #ffcc80 !important;
}
</style>